import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogContent } from '@angular/material/dialog';
import { Table } from '@cureskin/api-client';
import { ConnectionService } from 'src/services/connection-service';

@Component({
  selector: 'app-duplicate-ticket-modal',
  templateUrl: './duplicate-ticket-modal.component.html',
  styleUrls: ['./duplicate-ticket-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogContent]
})
export class DuplicateTicketModalComponent implements OnInit {
  selectedTickets: Array<Table.SupportTicket> = [];
  originalTicket: Table.SupportTicket | null = null;
  duplicateTickets: Array<Table.SupportTicket> = [];
  originalTicketInput: string = '';
  errorMessage: string = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { tickets: Array<Table.SupportTicket> },
    private dialogRef: MatDialogRef<DuplicateTicketModalComponent>,
    private connectionService: ConnectionService,
  ) {
    this.selectedTickets = data.tickets;
  }

  ngOnInit(): void {
    // Initialize with first ticket as potential original
    if (this.selectedTickets.length > 0) {
      this.originalTicket = this.selectedTickets[0];
      this.duplicateTickets = this.selectedTickets.slice(1);
    }
  }

  // Helper methods for template bindings
  isOriginalTicket(ticketId: string): boolean {
    return this.originalTicket?.id === ticketId;
  }

  isDuplicateTicket(ticketId: string): boolean {
    return this.duplicateTickets.some(t => t.id === ticketId);
  }

  canShowDuplicateSection(): boolean {
    return !!this.originalTicket;
  }

  canMarkAsDuplicates(): boolean {
    return !!this.originalTicket && this.duplicateTickets.length > 0;
  }

  getDuplicateTicketsCount(): number {
    return this.duplicateTickets.length;
  }

  onOriginalTicketChange(ticket: Table.SupportTicket): void {
    this.originalTicket = ticket;
    this.duplicateTickets = this.selectedTickets.filter(t => t.id !== ticket.id);
  }

  onDuplicateTicketChange(ticket: Table.SupportTicket, event: any): void {
    const isDuplicate = event.target.checked;
    if (isDuplicate) {
      if (!this.duplicateTickets.find(t => t.id === ticket.id)) {
        this.duplicateTickets.push(ticket);
      }
    } else {
      this.duplicateTickets = this.duplicateTickets.filter(t => t.id !== ticket.id);
    }
  }

  validateSelection(): boolean {
    if (!this.originalTicket) {
      this.errorMessage = 'Please select an original ticket';
      return false;
    }

    if (this.duplicateTickets.length === 0) {
      this.errorMessage = 'Please select at least one duplicate ticket';
      return false;
    }

    this.errorMessage = '';
    return true;
  }

  async markAsDuplicates(): Promise<void> {
    if (!this.validateSelection()) {
      return;
    }

    try {
      const result = await this.connectionService.markAsDuplicate(this.originalTicket?.id, this.duplicateTickets.map(t => t.id));
      this.dialogRef.close(result?.data?.message);
    } catch (error: any) {
      this.errorMessage = error.response?.data?.message || error.message || 'Error marking tickets as duplicates';
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  getTicketDisplayName(ticket: Table.SupportTicket): string {
    const title = ticket.get('title') || ticket.get('titleLanguageString')?.get('en') || 'No Title';
    const number = ticket.get('ticketNumber') || 'No Number';
    return `${number}: ${title}`;
  }
}
